import React, { useEffect, useState } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import api from "../../../services/api";
import Loading from "../../../components/Loader/Loading";
import MUIDataTable from "../../../components/MUIGrid";
import { FaSearch } from "react-icons/fa";
import {
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarDensitySelector,
  GridToolbarFilterButton,
} from "@mui/x-data-grid-pro";
import { Button as MUIButton } from "@mui/material";
import DateRangeComp from "../../../components/DateRangePicker";
import CustomBreadcrumbs from "../../../components/common/CustomBreadcrumbs";
import dayjs from "dayjs";

export default function MessagesReport() {
  const [isRecords, setRecords] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 100,
    page: 1,
  });
  const [queryOptions, setQueryOptions] = React.useState({
    groupOp: "",
    rules: [],
  });

  const initialFilters = () => {
    return {
      start_date: dayjs().subtract(7, "day").startOf("day").toDate(),
      end_date: new Date().toISOString().slice(0, 23) + "Z",
    };
  };

  const [filters, setFilters] = React.useState(initialFilters());

  const fetchListing = async (filter) => {
    if (!filter.loaderTerminated) {
      setIsLoading(true);
    }
    let payload = {
      ...filter,
      ...(filter?.resetFilter
        ? {
            start_date: dayjs().subtract(7, "day").startOf("day").toDate(),
            end_date: new Date().toISOString().slice(0, 23) + "Z",
          }
        : {
            ...filters,
          }),
    };

    if (!filter?.filters?.rules?.length) {
      delete payload.filters;
    }
    if (filter?.resetFilter) {
      delete payload.resetFilter;
    }
    delete payload.pageSize;
    delete payload?.loaderTerminated;
    try {
      const response = await api.post(
        `${process.env.REACT_APP_PUBLIC_API}/api/report/list_messages`,
        payload
      );
      setRecords(response.data);
    } catch (error) {
      console.error(`Error fetching  data: ${error}`);
    }
    if (!filter.loaderTerminated) {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      fetchListing({
        filters: queryOptions,
        loaderTerminated: true,
        ...paginationModel,
        perPage: paginationModel.pageSize,
      }),
    ])
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setIsLoading(false);
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columnDefs = [
    { headerName: "Sr#", field: "counter", width: 10, filterable: false },
    {
      field: "message_from",
      headerName: "From",
      width: 100,
      flex: 1,
    },
    {
      field: "message_to",
      headerName: "To",
      width: 100,
      flex: 1,
    },
    {
      field: "text",
      headerName: "Text",
      filterable: false,
      flex: 1,
      minWidth: 300,
      wrap: true,
      align: "left",
      cellClassName: "multiline-cell",
    },
  ];

  function CustomToolbar({ setFilterButtonEl }) {
    return (
      <GridToolbarContainer className="mb-3 mt-3 flex items-center justify-between">
        <div className="flex">
          <GridToolbarColumnsButton
            sx={{
              borderRadius: "6px",
              marginRight: "4px",
              border: "1px solid",
              borderColor: "#e8eaee",
              height: "26px",
              paddingLeft: 1,
              paddingRight: 1,
            }}
          />
          <GridToolbarDensitySelector
            sx={{
              borderRadius: "6px",
              marginRight: "4px",
              border: "1px solid",
              borderColor: "#e8eaee",
              height: "26px",
              paddingLeft: 1,
              paddingRight: 1,
            }}
          />
          <GridToolbarFilterButton
            sx={{
              borderRadius: "6px",
              marginRight: "4px",
              border: "1px solid",
              borderColor: "#e8eaee",
              height: "26px",
              paddingLeft: 1,
              paddingRight: 1,
            }}
            ref={setFilterButtonEl}
          />

          <MUIButton
            variant="text"
            onClick={handleNewFilter}
            startIcon={<FaSearch size={16} />}
            sx={{
              borderRadius: "6px",
              marginRight: "4px",
              border: "1px solid",
              borderColor: "#e8eaee",
              height: "26px",
              paddingLeft: 1,
              paddingRight: 1,
            }}
          >
            Apply filter
          </MUIButton>
        </div>
      </GridToolbarContainer>
    );
  }

  const operator = ({ operator, field }) => {
    const isSelect =
      columnDefs?.find((item) => item.field === field)?.type === "singleSelect";
    const isNumber =
      columnDefs?.find((item) => item.field === field)?.type === "number";
    const isDate =
      columnDefs?.find((item) => item.field === field)?.type === "date";
    return operator === "cn"
      ? "contains"
      : operator === "eq" && !isSelect && !isNumber
      ? "equals"
      : operator === "eq" && isSelect && !isNumber
      ? "is"
      : operator === "eq" && !isSelect && isNumber
      ? "="
      : operator === "not" && !isSelect && isNumber
      ? "!="
      : operator === "gt" && isDate
      ? "after"
      : operator === "gte" && isDate
      ? "onOrAfter"
      : operator === "lt" && isDate
      ? "before"
      : operator === "lte" && isDate
      ? "onOrBefore"
      : operator === "gt"
      ? ">"
      : operator === "gte"
      ? ">="
      : operator === "lt"
      ? "<"
      : operator === "lte"
      ? "<="
      : operator;
  };

  const [filterModel, setFilterModel] = React.useState({
    items: queryOptions.rules?.map(({ field, op, data }) => ({
      field,
      operator: operator({ operator: op, field }),
      value: data,
    })),
    logicOperator: "and",
    quickFilterValues: [],
    quickFilterLogicOperator: "and",
  });

  const processFilters = (filters) => {
    return (filters || [])?.map((filter) => {
      if (
        filter.operator === "isAnyOf" &&
        filter.value?.length &&
        filter.value[0]?.includes(",")
      ) {
        return {
          ...filter,
          value: filter.value[0].split(",").map((item) => item.trim()),
        };
      }
      return filter;
    });
  };

  const onFilterChange = React.useCallback(
    (filterModel) => {
      const item = processFilters(filterModel?.items || []);
      setFilterModel({
        ...filterModel,
        items: item,
      });
      let ruless = [];
      if (filterModel?.items?.length === 0) {
        fetchListing({
          filters: queryOptions,
          page: 1,
          perPage: paginationModel.pageSize,
        });
      }
      // eslint-disable-next-line
      processFilters(filterModel?.items)?.map((rule) => {
        ruless = [
          ...ruless,
          {
            field: `${rule.field}`,
            op:
              rule.operator === "contains"
                ? "cn"
                : rule.operator === "equals"
                ? "eq"
                : rule.operator === "is"
                ? "eq"
                : rule.operator === "="
                ? "eq"
                : rule.operator === "!="
                ? "not"
                : rule.operator === ">"
                ? "gt"
                : rule.operator === ">="
                ? "gte"
                : rule.operator === "<"
                ? "lt"
                : rule.operator === "<="
                ? "lte"
                : rule.operator === "onOrBefore"
                ? "lte"
                : rule.operator === "before"
                ? "lt"
                : rule.operator === "onOrAfter"
                ? "gte"
                : rule.operator === "after"
                ? "gt"
                : rule.operator,
            data: rule.value
              ? rule.value
              : rule.value === 0
              ? rule.value
              : rule.value === false
              ? rule.value
              : null,
          },
        ];
      });
      setQueryOptions({
        groupOp: filterModel?.logicOperator?.toUpperCase() || "AND",
        rules: ruless,
      });
      // eslint-disable-next-line
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const handleNewFilter = () => {
    setPaginationModel({ pageSize: paginationModel.pageSize, page: 1 });
    fetchListing({
      filters: queryOptions,
      page: 1,
      perPage: paginationModel.pageSize,
    });
  };

  const dataRows = (isRecords?.records || [])?.map((record, index) => {
    const counter = index + 1;
    const records = record;
    const { message_from, message_to, text, created_at } = record;

    return {
      counter,
      records,
      message_from,
      message_to,
      text,
      created_at,
    };
  });

  const handlePageSizeChange = (newPageSize) => {
    setPaginationModel({ ...paginationModel, pageSize: newPageSize });
  };
  const handlePageChange = (params) => {
    setPaginationModel({ pageSize: params.pageSize, page: params.page + 1 });
    fetchListing({
      filters: queryOptions,
      page: +params.page + 1,
      perPage: params.pageSize,
    });
  };

  const handleReset = () => {
    setFilterModel({
      ...filterModel,
      items: [],
    });
    setFilters((prevFilters) => ({
      ...prevFilters,
      start_date: dayjs().subtract(7, "day").startOf("day").toDate(),
      end_date: new Date().toISOString().slice(0, 23) + "Z",
    }));
    fetchListing({
      resetFilter: true,
      page: 1,
      perPage: 100,
    });
  };

  const getRowHeight = (params) => {
    const baseRowHeight = 40; 
    const lineHeight = 10; 
    const charPerLine = 60; 
    
    const text = params?.model?.text || '';
    const textLength = text.length;
    
    if (textLength === 0) return baseRowHeight;
    
    // Calculate number of lines needed for the text
    const numberOfLines = Math.ceil(textLength / charPerLine);
    
    // Calculate total height needed (base height + additional lines * line height)
    let calculatedHeight = baseRowHeight + (Math.max(0, numberOfLines - 1) * lineHeight);
    
    // Add extra padding if more than 3 lines
    const extraPadding = numberOfLines > 3 ? 16 : 0; // 8px top and 8px bottom
    
    // Ensure minimum height and add base padding
    return Math.max(baseRowHeight, calculatedHeight) + 8 + extraPadding;
  };
  return (
    <div className="px-6 pt-4">
      <CustomBreadcrumbs
        crumbs={[
          { label: "Home", link: "/" },
          { label: "Messages Report", link: "" },
        ]}
      />
      <ToastContainer />
      {isLoading && <Loading />}
      <div className="p-3 flex justify-end items-center w-full">
        <div className="flex items-center gap-x-2">
          <div className="flex items-center">
            <DateRangeComp
              inputClassName="!font-medium w-[220px] text-center bg-[#e9ecef] text-sm rounded-l !py-1 !px-2 border border-[#ced4da] !mb-0"
              pickerClassName="!left-1/2 !top-8 -translate-x-[75%]"
              dateRange
              onChange={(e) => {
                const endDate = new Date(e.selection.endDate);
                endDate.setHours(23, 59, 59, 59);
                const formattedEndDate =
                  endDate.toISOString().slice(0, 23) + "Z";

                const startDate = new Date(e.selection.startDate);
                startDate.setHours(0, 0, 0, 0);
                const formattedStartDate =
                  startDate.toISOString().slice(0, 23) + "Z";
                setFilters({
                  ...filters,
                  start_date: formattedStartDate,
                  end_date: formattedEndDate,
                });
              }}
              startDate={filters.start_date}
              endDate={filters.end_date}
            />
            <button
              variant="btn_cancel"
              className="h-[31px] !bg-[#042A42] rounded-r px-3 py-1.5 text-white"
              onClick={() => {
                fetchListing({
                  filters: queryOptions,
                  page: paginationModel.page,
                  perPage: paginationModel.pageSize,
                });
              }}
            >
              <FaSearch size={14} />
            </button>
          </div>

          <button
            className="btnPrimary h-[32px] bg-gray-600 text-white px-4 py-1.5 rounded"
            onClick={() => {
              setQueryOptions({
                groupOp: "",
                rules: [],
              });
              handleReset();
            }}
          >
            Reset
          </button>
        </div>
      </div>

      <div className="bg-white my-0 border rounded">
        <MUIDataTable
          columnDefs={columnDefs}
          items={dataRows}
          height="75vh"
          searchable="No"
          showCount="No"
          paginationMode="client"
          totalItems={isRecords?.totalRecords}
          onPaginationModelChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          paginationModel={paginationModel}
          onFilterModelChange={onFilterChange}
          CustomToolbar={CustomToolbar}
          filterModel={filterModel}
          gridOptions={getRowHeight}
        />
      </div>
    </div>
  );
}
